import React, { SyntheticEvent, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
    HStack,
    IconButton,
    IconButtonProps,
    Text,
    Tooltip,
    Whisper,
    WhisperProps,
} from "rsuite";

import CopyIcon from "@rsuite/icons/esm/react/Copy";

import constants from "utils/constants";

import "./Copy.less";

interface Props {
    children?: React.ReactNode;
    disabled?: boolean;
    placement?: WhisperProps["placement"];
    size?: IconButtonProps["size"];
    value?: string;
}

const Copy: React.FC<Props> = ({
    children,
    disabled,
    placement = "top",
    size = "md",
    value,
}: Props) => {
    const { t } = useTranslation();

    const before = t("copyToClipboard.copy");
    const after = t("copyToClipboard.copied");

    const [message, setMessage] = React.useState<string>(before);

    const handleCopyClick = (event: SyntheticEvent) => {
        event.stopPropagation();
        if (value) {
            navigator.clipboard.writeText(value).then(() => {
                setMessage(after);
            });
        }
    };

    const speaker = useMemo(
        () => (
            <Tooltip>
                <Text style={{ color: "white" }}>{message}</Text>
            </Tooltip>
        ),
        [message]
    );

    const copyButton = (
        <Whisper
            trigger={"hover"}
            placement={placement}
            delay={constants.defaultDelay}
            speaker={speaker}
            onExited={() => setMessage(before)}
            enterable
        >
            <IconButton
                icon={<CopyIcon />}
                onClick={handleCopyClick}
                className={"copy__icon"}
                appearance="subtle"
                size={size}
                disabled={disabled}
            />
        </Whisper>
    );

    if (!children) {
        return copyButton;
    }

    return (
        <HStack justifyContent={"flex-start"} spacing={10}>
            <HStack.Item alignSelf={"center"}>{children}</HStack.Item>
            <HStack.Item alignSelf={"center"}>{copyButton}</HStack.Item>
        </HStack>
    );
};

export default Copy;
